import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from 'components';
import CancelIcon from '@mui/icons-material/Cancel';
import DependancyGraph from 'pages/configuration/AnalysisConfiguration/WorkflowMonitor/dependancyGraph';
import { useEffect, useMemo, useState } from 'react';
import { ConfigSchema } from 'schemas';
import { AxiosResponse } from 'axios';
import {
  useDeleteWorkflowConfiguration,
  useGetAllActivities,
  useGetWorkflowDetail,
  useUpdateWorkflowConfiguration
} from 'api/sdk';
import { object, string } from 'yup';
import { Skeleton } from '@mui/material';
import { setObjectMapping } from 'store/annotations/annotationSlice';
import { useDispatch } from 'react-redux';

interface ModalType {
  handleChange: (selectedValue: string) => void;
  selectedWorkflowId: string;
  selectedModel: any;
  getConfigurationData: AxiosResponse<ConfigSchema, any> | undefined;
  getModelId: string;
  isOpen: any;
  currentConfigId: string;
  setIsOpen: any;
  setSelectedWorkflowId: React.Dispatch<React.SetStateAction<string>>;
  setSelectedModel: React.Dispatch<React.SetStateAction<any[]>>;
}

const WorkflowModal = ({
  setIsOpen,
  setSelectedWorkflowId,
  setSelectedModel,
  handleChange,
  selectedWorkflowId,
  selectedModel,
  getConfigurationData,
  getModelId,
  isOpen,
  currentConfigId
}: ModalType) => {
  const workflowSchema = object({
    name: string()
      .required('Workflow name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(20, 'Name must not exceed 20 characters')
  });

  const navigate = useNavigate();
  const { refetch: workflowRefetch, loading: fetching }: any =
    useGetWorkflowDetail(getModelId);
  const { data: allActivities } = useGetAllActivities(currentConfigId || '', {
    pipeline_satus: true || null
  });

  const { mutateAsync: deleteWorkflow, isLoading: loadingDelete } =
    useDeleteWorkflowConfiguration();
  const [editableData, setEditableData] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const { mutateAsync, isLoading } = useUpdateWorkflowConfiguration();
  const [editableWorkflowId, setEditableWorkflowId] = useState<string | null>(
    null
  );
  const [isDelete, setDelete] = useState(false);
  const [wfData, setWfData] = useState<any>([]);
  const dispatch = useDispatch();

  const handleEditClick = (workflowId: string, name: string) => {
    setEditableWorkflowId(workflowId);
    setEditableData(name);
    setValidationError('');
  };

  function handleCloseWorkflow() {
    setIsOpen({ status: false, data: [] });
    setSelectedWorkflowId('');
    setSelectedModel([]);
    setEditableWorkflowId(null);
    setEditableData('');
    setValidationError('');
  }

  console.log('workflowdata', wfData);

  const handleInputChange = (value: string) => {
    setEditableData(value);
    try {
      workflowSchema.validateSync({ name: value });
      setValidationError('');
    } catch (err: any) {
      setValidationError(err.message);
    }
  };

  const handleSave = async (id: string): Promise<void> => {
    if (!id) return;

    try {
      await workflowSchema.validate({ name: editableData });

      await mutateAsync({
        configId: currentConfigId || '',
        workflowId: selectedWorkflowId,
        data: {
          workflow_config: selectedModel[0].data.workflow,
          name: editableData
        }
      });

      setEditableWorkflowId(null);
      setValidationError('');

      const response = await workflowRefetch();
      setWfData(response?.data?.data);
    } catch (err: any) {
      console.log('error of update', err);
      setValidationError(err.response.data.detail);
    }
  };

  const handleDeleteModel = async (): Promise<void> => {
    await deleteWorkflow({
      configId: currentConfigId,
      workflowId: selectedWorkflowId
    }).then(() =>
      workflowRefetch().then((response: any) => {
        setWfData(response?.data?.data);
        setDelete(false);
        handleChange(response?.data?.data[0].id);
      })
    );
  };

  useEffect(() => {
    setWfData([]);
    isOpen?.status &&
      workflowRefetch().then((response: any) => {
        setWfData(response?.data?.data);
        setSelectedWorkflowId(response?.data?.data[0]?.id);
      });
  }, [isOpen]);

  const nodes = useMemo(() => {
    if (!selectedModel.length) {
      return [];
    }
    const modelData = selectedModel[0]?.data;
    if (!modelData?.workflow || !Array.isArray(modelData.workflow)) {
      return [];
    }

    if (getConfigurationData?.data?.config_type === 'pick_and_place') {
      const workflowConfig = modelData.workflow[0]?.workflow_config;
      if (!workflowConfig || !Array.isArray(workflowConfig)) {
        return [];
      }
      return workflowConfig.map((item: any) => ({
        id: item.id,
        data: {
          label: item.label,
          mandatory: item.mandatory
        },
        position: {
          x: 200 * (Math.random() * 2),
          y: 200 * (Math.random() * 2)
        }
      }));
    } else {
      return (
        wfData &&
        wfData.filter((data: any) => data?.id === selectedWorkflowId) &&
        wfData
          .filter((data: any) => data?.id === selectedWorkflowId)[0]
          ?.data?.workflow.map((item: any, index: number) => ({
            id: item.id,
            data: {
              label:
                allActivities?.data?.activity_details?.find(
                  activity => activity.id === item?.activity_id
                )?.activity ?? '',
              mandatory: item.mandatory || false
            },
            position: {
              x: 200 * (index % 3),
              y: Math.floor(index / 3) * 100
            }
          }))
      );
    }
  }, [selectedModel, getConfigurationData, wfData, selectedWorkflowId]);

  const edges = useMemo(() => {
    if (!selectedModel.length) {
      return [];
    }

    const modelData = wfData;

    const uniqueEdges = new Set<string>();
    const allEdges: any[] = [];

    if (getConfigurationData?.data?.config_type === 'pick_and_place') {
      const workflowConfig =
        selectedModel[0]?.data?.workflow[0]?.workflow_config;

      if (!workflowConfig) {
        return [];
      }

      return workflowConfig
        .filter(
          (item: any) => item.dependency && Array.isArray(item.dependency)
        )
        .flatMap((item: any) =>
          item.dependency.map((dep: any) => ({
            id: `e-${dep}-${item.id}`,
            source: dep,
            target: item.id,
            type: 'smoothstep',
            animated: true
          }))
        );
    } else {
      wfData &&
        wfData.filter((data: any) => data?.id === selectedWorkflowId) &&
        wfData
          .filter((data: any) => data?.id === selectedWorkflowId)[0]
          ?.data?.workflow?.forEach((item: any) => {
            if (item.dependency && Array.isArray(item.dependency)) {
              item?.dependency?.forEach((dep: string) => {
                const edgeKey = `${dep}-${item.id}`;
                if (!uniqueEdges.has(edgeKey)) {
                  uniqueEdges.add(edgeKey);
                  allEdges.push({
                    id: `e-${dep}-${item.id}`,
                    source: dep,
                    target: item.id
                  });
                }
              });
            }
          });
    }

    return allEdges;
  }, [selectedModel, getConfigurationData, wfData, selectedWorkflowId]);

  return (
    <>
      <Modal open={isOpen.status} onClose={handleCloseWorkflow} size="lg">
        <div className="flex flex-row items-start justify-between gap-4 w-full">
          <div className="border-2 border-[#939393] flex flex-1 flex-col bg-[#eeeeee] w-[60%]">
            {nodes && edges && <DependancyGraph nodes={nodes} edges={edges} />}
          </div>
          <div className="flex flex-1 flex-col w-[30%] h-full justify-between">
            <div className="h-[50vh] flex flex-col items-center">
              <div className="w-full bg-[#102633] text-white py-2 flex items-center justify-start pl-[10px]">
                Workflow List
              </div>
              <div className="w-full h-[50vh] overflow-auto">
                {wfData &&
                  wfData?.map((workflow: any) => {
                    const isSelected = workflow.id === selectedWorkflowId;
                    const isEditing = workflow.id === editableWorkflowId;
                    return (
                      <div
                        key={workflow.id}
                        className={`w-full py-2 flex flex-col items-center justify-between cursor-pointer transition-all duration-200
                      ${
                        isSelected
                          ? 'bg-[#eeeeee] text-black'
                          : 'bg-[#ffffff] text-black'
                      }
                      pl-[10px] pr-[10px] hover:bg-[#eeeeee]`}
                      >
                        <div
                          className="w-full flex items-center justify-between"
                          onClick={() => handleChange(workflow.id)}
                        >
                          {!isEditing ? (
                            <div>{workflow?.name || 'Unnamed'}</div>
                          ) : (
                            <input
                              value={editableData}
                              onChange={e => handleInputChange(e.target.value)}
                              className="bg-[#eeeeee] border-b border-gray-500 focus:outline-none"
                            />
                          )}

                          <div className="flex">
                            {!isEditing ? (
                              <EditIcon
                                className="fill-[#102633] hover:bg-[#102633] hover:fill-white rounded h-[20px] w-[20px] mr-[10px]"
                                onClick={() => {
                                  handleChange(workflow.id);
                                  handleEditClick(workflow.id, workflow.name);
                                }}
                              />
                            ) : (
                              <div>
                                <CancelIcon
                                  className="fill-[#102633] hover:bg-[#102633] hover:fill-white rounded h-[20px] w-[20px] mr-[10px]"
                                  onClick={() => {
                                    setSelectedWorkflowId('');
                                    setSelectedModel([]);
                                    setEditableWorkflowId(null);
                                    setEditableData('');
                                    setValidationError('');
                                  }}
                                />
                                <SaveOutlinedIcon
                                  className={`fill-[#102633] hover:bg-[#102633] hover:fill-white rounded h-[20px] w-[20px] mr-[10px] ${
                                    validationError
                                      ? 'opacity-50 cursor-not-allowed'
                                      : ''
                                  }`}
                                  onClick={() => {
                                    if (!validationError) {
                                      handleChange(workflow.id);
                                      handleSave(workflow.id);
                                    }
                                  }}
                                />
                              </div>
                            )}

                            <DeleteIcon
                              className={`${
                                wfData.length > 1
                                  ? 'hover:bg-[#102633] hover:fill-white fill-[#102633]'
                                  : '!cursor-not-allowed'
                              } rounded h-[20px] w-[20px]`}
                              onClick={() => {
                                wfData.length > 1 && handleChange(workflow.id);
                                wfData.length > 1 && setDelete(true);
                              }}
                            />
                          </div>
                        </div>
                        {isEditing && validationError && (
                          <div className="text-red-500 text-xs mt-1 w-full">
                            {validationError}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              {wfData.length === 0 && (
                <Skeleton
                  variant="rectangular"
                  width={'100%'}
                  height={30}
                  style={{
                    textAlign: 'center',
                    alignContent: 'center'
                  }}
                >
                  Loading....
                </Skeleton>
              )}
            </div>
            <div className="flex items-center justify-center gap-4 mt-4 px-4 pb-4">
              <button
                color="primary"
                onClick={() => {
                  if (
                    getConfigurationData?.data?.config_type === 'pick_and_place'
                  ) {
                    navigate(
                      `/train/workflowconfig/pick&place?method=edit&workflowId=${selectedWorkflowId}&modelId=${getModelId}`
                    );
                  } else {
                    navigate(
                      `/train/workflowconfig/spatiotemporal?method=edit&workflowId=${selectedWorkflowId}`
                    );
                  }
                }}
                className="bg-[#ffffff] px-6 py-2 text-[#102433] text-sm min-w-[120px] max-w-[200px] rounded-sm flex items-center justify-center gap-2 border border-[#102433] hover:bg-[#f5f5f5] transition-colors"
                disabled={false}
              >
                Edit Workflow
              </button>
              <button
                onClick={() => {
                  if (
                    getConfigurationData?.data?.config_type === 'pick_and_place'
                  ) {
                    dispatch(
                      setObjectMapping(
                        wfData[0]['data']?.workflow[0]['object_mapping']
                      )
                    );
                    navigate(
                      `/train/workflowconfig/pick&place?method=new&modelId=${getModelId}`
                    );
                  } else {
                    navigate(
                      `/train/workflowconfig/:spatiotemporal?method=new&workflowId=${selectedWorkflowId}&modelId=${getModelId}`
                    );
                  }
                }}
                disabled={false}
                className="bg-[#102433] px-6 py-2 text-white text-sm min-w-[120px] max-w-[200px] rounded-sm flex items-center justify-center gap-2 border border-[#102433] hover:bg-[#1a3a4f] transition-colors"
              >
                Add New Workflow
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal open={isDelete} onClose={() => setDelete(false)} size="sm">
        <div>
          <div className="font-bold mb-[10px]">
            Are you sure you want to delete this Workflow?
          </div>
          <div className="flex justify-between">
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setDelete(false)}
              version="light"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleDeleteModel}
              version="dark"
              loading={loadingDelete || fetching}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default WorkflowModal;
